import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import {
  dataList,
  displayDate,
  intelexRequest,
  reportDetailPath,
} from "../intelex/api";

function reviewLabel(value) {
  return (
    {
      pending: "Pending review",
      accepted: "Accepted",
      rejected: "Rejected",
      invalidated: "PIREP invalidated",
    }[value] || "Pending review"
  );
}

function ReportsList({ user }) {
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [state, setState] = useState({ loading: true, error: "" });
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    let current = true;
    intelexRequest("/api/portal/reports")
      .then((payload) => {
        if (!current) return;
        setReports(dataList(payload));
        setState({ loading: false, error: "" });
      })
      .catch((error) => {
        if (current)
          setState({
            loading: false,
            error: error.message || "Reports could not be loaded.",
          });
      });
    return () => {
      current = false;
    };
  }, []);

  const filtered = useMemo(
    () =>
      reports.filter((report) => {
        const statusMatches =
          status === "all" || (report.reviewStatus || "pending") === status;
        const search =
          `${report.eventNumber || ""} ${report.template?.title || report.templateTitle || report.title || ""} ${report.template?.category || report.category || ""}`.toLowerCase();
        return statusMatches && search.includes(query.toLowerCase());
      }),
    [query, reports, status],
  );

  return (
    <div className="intelex-main intelex-reports-page">
      <header className="intelex-page-heading">
        <div>
          <h1>My reports</h1>
          <p>Track reports you have personally submitted to the safety team.</p>
        </div>
        <Link className="intelex-primary-button" to="/interep">
          Create report
        </Link>
      </header>
      {location.state?.submitted && (
        <div className="intelex-success" role="status">
          <strong>Report submitted</strong>
          <span>Your report was recorded successfully.</span>
        </div>
      )}
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <section className="intelex-panel">
        <div className="intelex-report-filters">
          <label>
            <span className="sr-only">Search reports</span>
            <input
              type="search"
              placeholder="Search reference or report type"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">Filter by review state</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All review states</option>
              <option value="pending">Pending review</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="invalidated">PIREP invalidated</option>
            </select>
          </label>
        </div>
        {state.loading ? (
          <div className="intelex-loading" role="status">
            Loading reports…
          </div>
        ) : filtered.length ? (
          <div className="intelex-table-scroll">
            <table className="intelex-table">
              <thead>
                <tr>
                  <th>Event number</th>
                  <th>Report type</th>
                  <th>Category</th>
                  <th>Submitted</th>
                  <th>Linked flight</th>
                  <th>Review</th>
                  <th>
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((report) => (
                  <tr
                    key={report.id}
                    className={report.responseRequired ? "requires-response" : ""}
                  >
                    <td>
                      <strong>
                        {report.eventNumber ||
                          report.reference ||
                          `#${report.id}`}
                      </strong>
                      {report.isTestSubmission && (
                        <span className="intelex-test-badge">Test</span>
                      )}
                    </td>
                    <td>
                      {report.template?.title ||
                        report.templateTitle ||
                        report.title ||
                        "Safety report"}
                    </td>
                    <td>
                      {report.template?.category || report.category || "—"}
                    </td>
                    <td>
                      {displayDate(report.submittedAt || report.createdAt)}
                    </td>
                    <td>{report.pirepId ? `PIREP #${report.pirepId}` : "—"}</td>
                    <td>
                      <span className="intelex-list-state">
                        {reviewLabel(report.reviewStatus)}
                      </span>
                      {report.responseRequired && (
                        <span className="intelex-pilot-response-badge">
                          Your response is required
                        </span>
                      )}
                    </td>
                    <td>
                      <Link
                        className="intelex-table-link"
                        to={reportDetailPath(report)}
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="intelex-empty">
            <strong>No reports found</strong>
            <p>
              {reports.length
                ? "Try changing the filters."
                : "Submitted reports will appear here."}
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

export default function IntelexReportsPage() {
  return (
    <IntelexFrame active="reports" title="Submitted reports">
      {({ user }) => <ReportsList user={user} />}
    </IntelexFrame>
  );
}
